/**
 * React Router state for broker/shipper carrier partner list → profile → back.
 * Keeps PO carrier-assignment workflow fields in sync across list and details pages.
 *
 * Production: also use {@link PO_CARRIER_ASSIGNMENT_QUERY_KEY} on the URL so assignment
 * context survives refresh, new tabs, and back/forward without relying on history state alone.
 */

/** Query param: Firestore document id of the PO pending carrier assignment */
export const PO_CARRIER_ASSIGNMENT_QUERY_KEY = 'assignPo';

export type PoCarrierPartnerLocationState = {
  sourcePage: string;
  poData?: unknown;
  rate?: unknown;
  isEditing?: boolean;
  editingPO?: unknown;
  fromRejection?: boolean;
  rejectedLoadId?: string;
};

/** Broker sidebar + canonical list URL */
export const BROKER_CARRIER_PARTNERS_LIST_PATH = '/broker/carrier-partners';

/** Shipper has two list URLs for the same screen (see ShipperPages routes). */
export const SHIPPER_CARRIER_PARTNERS_LIST_PATH = '/shipper/carrier-partners';
export const SHIPPER_PARTNERS_LIST_PATH_LEGACY = '/shipper/partners';

/** Which partner-grid URL the user is on (for correct "back" from carrier profile). */
export function shipperCarrierPartnerListPath(pathname: string): string {
  return pathname === SHIPPER_CARRIER_PARTNERS_LIST_PATH
    ? SHIPPER_CARRIER_PARTNERS_LIST_PATH
    : SHIPPER_PARTNERS_LIST_PATH_LEGACY;
}

export function getAssignPoFromSearch(search: string): string | null {
  if (!search) return null;
  const q = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(q);
  const v = params.get(PO_CARRIER_ASSIGNMENT_QUERY_KEY);
  return v && v.trim() ? v.trim() : null;
}

/** Append or replace assignPo on a path that may already have a query string. */
export function withAssignPoQuery(path: string, poFirestoreId: string): string {
  const [pathname, existingQuery = ''] = path.split('?');
  const params = new URLSearchParams(existingQuery);
  params.set(PO_CARRIER_ASSIGNMENT_QUERY_KEY, poFirestoreId);
  const serialized = params.toString();
  return serialized ? `${pathname}?${serialized}` : pathname;
}

/** Navigate state when opening `/…/carrier-partners/:id` from the partner grid. */
export function buildNavigateStateToCarrierProfile(
  partnersListPath: string,
  workflow?: {
    poData: unknown;
    rate?: unknown;
    isEditing?: boolean;
    editingPO?: unknown;
    fromRejection?: boolean;
    rejectedLoadId?: string;
  } | null
): PoCarrierPartnerLocationState {
  const out: PoCarrierPartnerLocationState = { sourcePage: partnersListPath };
  if (workflow?.poData != null) {
    out.poData = workflow.poData;
    if (workflow.rate !== undefined) out.rate = workflow.rate;
    if (workflow.isEditing !== undefined) out.isEditing = workflow.isEditing;
    if (workflow.editingPO !== undefined) out.editingPO = workflow.editingPO;
    if (workflow.fromRejection !== undefined) out.fromRejection = workflow.fromRejection;
    if (workflow.rejectedLoadId !== undefined) out.rejectedLoadId = workflow.rejectedLoadId;
  }
  return out;
}

/** Restore list URL + optional PO workflow state after viewing carrier profile. */
export function navigationBackFromCarrierProfile(
  locationState: unknown,
  fallbackListPath: string,
  options?: { currentSearch?: string }
): { to: string; state?: Record<string, unknown> } {
  const assignPoFromUrl = options?.currentSearch
    ? getAssignPoFromSearch(options.currentSearch)
    : null;
  const s =
    locationState && typeof locationState === 'object'
      ? (locationState as Partial<PoCarrierPartnerLocationState>)
      : null;
  let to =
    typeof s?.sourcePage === 'string' && s.sourcePage.length > 0 ? s.sourcePage : fallbackListPath;
  if (assignPoFromUrl) {
    to = withAssignPoQuery(to, assignPoFromUrl);
  }
  if (s?.poData != null) {
    return {
      to,
      state: {
        poData: s.poData,
        ...(s.rate !== undefined ? { rate: s.rate } : {}),
        ...(s.isEditing !== undefined ? { isEditing: s.isEditing } : {}),
        ...(s.editingPO !== undefined ? { editingPO: s.editingPO } : {}),
        ...(s.fromRejection !== undefined ? { fromRejection: s.fromRejection } : {}),
        ...(s.rejectedLoadId !== undefined ? { rejectedLoadId: s.rejectedLoadId } : {}),
      },
    };
  }
  return { to };
}
